import React, { useState } from "react";
import { toast } from "sonner";
import { MessageCircle, RotateCw } from "lucide-react";
import { Input, Label } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";

export const ResetStep = ({ phone: initial, onDone, onBack }: { phone: string; onDone: () => void; onBack: () => void }) => {
  const { t } = usePrefs();
  const [phone, setPhone] = useState(initial);
  const [sent, setSent] = useState(false);
  const [f, setF] = useState({ code: "", pw: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError("");
    try { await fn(); } catch (err) { setError(errorText(err, t)); } finally { setBusy(false); }
  };
  const request = (e?: React.FormEvent) => { e?.preventDefault(); run(async () => { await api.post("/auth/customer/reset/request", { phone }); setSent(true); toast.success(t("rs.requested")); }); };
  const confirm = (e: React.FormEvent) => {
    e.preventDefault();
    if (f.pw !== f.confirm) return setError(t("sec.mismatch"));
    run(async () => { await api.post("/auth/customer/reset/confirm", { phone, code: f.code, new_password: f.pw }); toast.success(t("rs.done")); onDone(); });
  };
  const inp = (k: keyof typeof f, label: string, props: Record<string, unknown>) => (
    <div className="space-y-1.5">
      <Label htmlFor={`rs-${k}`}>{t(label)}</Label>
      <Input id={`rs-${k}`} dir="ltr" required value={f[k]} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: k === "code" ? e.target.value.replace(/\D/g, "").slice(0, 6) : e.target.value })}
        data-testid={`reset-${k}-input`} className="h-12 rounded-2xl" {...props} />
    </div>
  );
  const err = error && <p data-testid="reset-error" className="text-sm text-primary font-semibold">{error}</p>;
  const btn = (label: string, dis = false) => <button disabled={busy || dis} data-testid="reset-submit" className="w-full h-12 rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-60">{busy ? t("common.loading") : t(label)}</button>;

  return (
    <div className="space-y-4" data-testid="reset-step">
      {!sent ? (
        <form onSubmit={request} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="rs-phone">{t("cl.phone")}</Label>
            <Input id="rs-phone" dir="ltr" type="tel" inputMode="tel" required value={phone} placeholder="07XXXXXXXXX" onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)} data-testid="reset-phone-input" className="h-12 rounded-2xl" />
          </div>
          {err}{btn("rs.request")}
          <button type="button" onClick={() => phone ? setSent(true) : setError(t("cl.needPhone"))} data-testid="reset-have-code" className="block text-sm text-muted-foreground font-semibold">{t("rs.haveCode")}</button>
        </form>
      ) : (
        <form onSubmit={confirm} className="space-y-4">
          <div className="rounded-2xl bg-accent/60 p-4 text-sm flex gap-3">
            <MessageCircle className="h-5 w-5 text-[#25D366] shrink-0 mt-0.5" />
            <p>{t("rs.note")} <b dir="ltr">{phone}</b></p>
          </div>
          {inp("code", "rs.code", { inputMode: "numeric", autoComplete: "one-time-code", maxLength: 6, minLength: 6, placeholder: "••••••" })}
          {inp("pw", "sec.new", { type: "password", autoComplete: "new-password", minLength: 8, maxLength: 128 })}
          {inp("confirm", "sec.confirm", { type: "password", autoComplete: "new-password", minLength: 8, maxLength: 128 })}
          {err}{btn("rs.save", f.code.length !== 6)}
          <button type="button" onClick={() => request()} data-testid="reset-resend" className="inline-flex items-center gap-1.5 text-sm font-bold text-primary"><RotateCw className="h-4 w-4" />{t("cl.resend")}</button>
        </form>
      )}
      <button type="button" onClick={onBack} data-testid="reset-back" className="text-sm text-muted-foreground font-semibold">{t("cl.back")}</button>
    </div>
  );
};
